/**
 * 複数ファイルのアップロード待ち行列。
 *
 * 【なぜ順番に流すのか】
 * マスターは Box への分割アップロードで、1本で数GBになる。同時に走らせると
 * 上り帯域を取り合って**全部が遅くなり、どれも終わらない**という最悪の形になる。
 * 1本ずつ流せば、終わったものから順に使えるようになる。
 *
 * 【端末による制限】
 * CLAUDE.md 原則4 に基づき、**スマートフォンからの動画マスターは受け付けない**。
 * iOS Safari は画面を離れると接続を切るため、数GBの分割アップロードが
 * 途中で必ず壊れる。写真は数MBで一度に送り切れるので許可する。
 */

export type UploadKind = 'video' | 'image'

export type QueueItemStatus = 'waiting' | 'uploading' | 'succeeded' | 'failed' | 'rejected'

export type QueueItem = {
  id: string
  file: File
  kind: UploadKind
  status: QueueItemStatus
  /** 0-100。算出できない間は null。 */
  percent: number | null
  uploadedBytes: number
  error: string | null
  /** 受け付けられない理由（端末制限・サイズ超過など）。 */
  rejection: string | null
  assetId: string | null
}

/** Box Enterprise の1ファイル上限（50GiB）。 */
export const MASTER_MAX_BYTES = 50 * 1024 * 1024 * 1024

/** 静止画マスターの上限。これを超えるものは写真ではなく別物とみなす。 */
export const IMAGE_MAX_BYTES = 200 * 1024 * 1024

/**
 * スマートフォン／タブレットか。
 *
 * User-Agent ではなく**入力方式**で判定する。UA は詐称も変更も多いが、
 * 「粗いポインタしか無く、タッチ操作である」端末は実際にモバイルである確率が高い。
 * 判定を誤っても、写真は通り動画だけが弾かれる（安全側）。
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(pointer: coarse)').matches && window.matchMedia('(hover: none)').matches
}

export function classifyFile(file: File): UploadKind | null {
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('image/')) return 'image'
  // iOS から来る HEIC/HEIF は type が空になることがある
  if (/\.(heic|heif)$/i.test(file.name)) return 'image'
  if (/\.(mov|mp4|m4v|avi|mxf|mkv|webm)$/i.test(file.name)) return 'video'
  if (/\.(jpe?g|png|tiff?|webp|dng|cr[23]|nef|arw)$/i.test(file.name)) return 'image'
  return null
}

/**
 * 受け付けられるかを判定し、駄目な理由を日本語で返す。
 *
 * @param mobile モバイル端末か（isMobileDevice() の結果を渡す。テストのため引数にしている）
 */
export function rejectionReason(file: File, kind: UploadKind | null, mobile: boolean): string | null {
  if (kind === null) {
    return '対応していない形式です（動画または画像を選んでください）'
  }
  if (kind === 'video') {
    if (mobile) {
      // 原則4。ここを緩めると「3時間アップロードして最後に失敗する」事故になる。
      return 'スマートフォンからの動画アップロードには対応していません。PCブラウザから送ってください'
    }
    if (file.size > MASTER_MAX_BYTES) {
      return `1ファイル 50GB を超えています（${formatBytes(file.size)}）`
    }
  }
  if (kind === 'image' && file.size > IMAGE_MAX_BYTES) {
    return `画像としては大きすぎます（${formatBytes(file.size)}）`
  }
  if (file.size === 0) {
    return '中身が空のファイルです'
  }
  return null
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`
}

/** ファイル名から素材タイトルの初期値を作る（拡張子を落とすだけ）。 */
export const titleFromFileName = (name: string) => name.replace(/\.[^.]+$/, '')

let counter = 0
export function toQueueItems(files: File[], mobile: boolean): QueueItem[] {
  return files.map((file) => {
    const kind = classifyFile(file)
    const rejection = rejectionReason(file, kind, mobile)
    return {
      // File には安定したIDが無いので採番する。名前だけだと同名ファイルで衝突する。
      id: `q${++counter}-${file.name}-${file.size}`,
      file,
      kind: kind ?? 'image',
      status: rejection ? 'rejected' : 'waiting',
      percent: null,
      uploadedBytes: 0,
      error: null,
      rejection,
      assetId: null,
    }
  })
}

/** DataTransfer からファイルを取り出す（ディレクトリは無視する）。 */
export function filesFromDataTransfer(dt: DataTransfer): File[] {
  if (dt.items && dt.items.length > 0) {
    return Array.from(dt.items)
      .filter((i) => i.kind === 'file')
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null)
  }
  return Array.from(dt.files ?? [])
}

/** 待ち行列全体の進み具合。 */
export function queueSummary(items: QueueItem[]) {
  const active = items.filter((i) => i.status === 'waiting' || i.status === 'uploading')
  const done = items.filter((i) => i.status === 'succeeded')
  const failed = items.filter((i) => i.status === 'failed')
  const rejected = items.filter((i) => i.status === 'rejected')
  const totalBytes = items
    .filter((i) => i.status !== 'rejected')
    .reduce((sum, i) => sum + i.file.size, 0)
  const uploadedBytes = items.reduce(
    (sum, i) => sum + (i.status === 'succeeded' ? i.file.size : i.uploadedBytes),
    0,
  )
  return {
    active: active.length,
    done: done.length,
    failed: failed.length,
    rejected: rejected.length,
    total: items.length,
    percent: totalBytes > 0 ? Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)) : 0,
    running: active.length > 0,
  }
}
